import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useStrukturaLabels, formatStruktura } from "@/components/slot/MaterialSelect";
import {
  PRAZDNY_STAV,
  VyberPolozkyDialog,
  type StavPolozky,
} from "@/components/import/VyberPolozkyDialog";
import { metreHrany, vetaKontextu } from "@/lib/import/vyskyty";
import { useEdgeCandidates } from "@/api/hooks/useCennik";
import { formatEur, materialPrimaryId, rozmerHrany } from "@/lib/format";
import type { Predvyplnenie } from "@/components/cennik/MaterialEditDialog";
import type { MapovaniePolozka } from "@/api/hooks/useTopsolidRaw";
import {
  ZapamataneStitok,
  ZapamatatPrepinac,
  ZoZakazkyStitok,
  jeZapamatane,
  jeZoZakazky,
} from "@/components/import/Zapamatanie";

/**
 * Hrany po dekoroch — rovnaká tabuľka, akú má slot.
 *
 * Import ich dovtedy vypisoval ako plochý zoznam riadkov („22x05 na
 * K682"), takže sa nedalo prečítať to, na čo sa dielňa pýta: **čo má
 * tento dekor za hrany**. Slot to rieši tabuľkou hrúbka dielca × hrúbka
 * hrany a je to zabehnutý pohľad — tu je ten istý, len sa priraďuje do
 * importu namiesto do slotu.
 *
 * Pod tabuľkou je zbalený zoznam **detegovaných** hrán dekoru (to, čo
 * appka v cenníku pre dekor našla) — presne ako v slot dialógu.
 */

/** Ten istý tvar ako pri materiáloch — jeden typ, nie dva rovnaké. */
export type StavHrany = StavPolozky;

/** „18.00" → „18"; hrúbky chodia ako Decimal string. */
function mm(v: string | null | undefined): string {
  if (v == null) return "—";
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : v;
}

function kluc(p: MapovaniePolozka): string {
  return `${p.druh}|${p.token}|${p.kontext}`;
}

/**
 * Hrúbka, ktorú export nepovedal.
 *
 * Riadok/stĺpec pre ne **musí** existovať: hrana bez hrúbky dielca by
 * inak z tabuľky ticho vypadla — aj keby bola nepriradená a blokovala
 * import. Nezaradená položka je horšia než škaredý riadok.
 */
const NEZNAMA = "?";

/** Zoradené unikátne hrúbky; neznáme skončia na konci pod `NEZNAMA`. */
function unikatneHrubky(hodnoty: (string | null)[]): string[] {
  const map = new Map<number, string>();
  let neznama = false;
  for (const h of hodnoty) {
    const n = h == null ? NaN : Number(h);
    if (!Number.isFinite(n)) {
      neznama = true;
      continue;
    }
    if (!map.has(n)) map.set(n, h as string);
  }
  const out = [...map.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, v]) => v);
  return neznama ? [...out, NEZNAMA] : out;
}

/** Čo o priradenej doske vie mapovacia obrazovka. */
export interface PriradenaDoska {
  id: string;
  /** Kód alebo názov z cenníka — čo user reálne vybral. */
  popis: string | null;
}

type Skupina = {
  /** Normalizovaný token materiálu — zhoduje sa s `token` položky materiálu. */
  token: string;
  nazov: string;
  polozky: MapovaniePolozka[];
};

export function skupinyHran(hrany: MapovaniePolozka[]): Skupina[] {
  const map = new Map<string, Skupina>();
  for (const p of hrany) {
    // `kontext` hrany JE normalizovaný materiál (`kluc_hrany` na serveri),
    // takže sa páruje s `token` materiálovej položky bez normalizácie v TS
    let g = map.get(p.kontext);
    if (!g) {
      g = { token: p.kontext, nazov: p.material_popis || p.kontext, polozky: [] };
      map.set(p.kontext, g);
    }
    g.polozky.push(p);
  }
  return [...map.values()].sort((a, b) => a.nazov.localeCompare(b.nazov));
}

export function TabulkaHran({
  hrany,
  stavy,
  nazvyVybranych,
  onZmena,
  doskaPreToken,
  predvyplneniePre,
}: {
  hrany: MapovaniePolozka[];
  stavy: Record<string, StavHrany>;
  /** Názvy ručne vybraných hrán — picker ich vracia pri výbere. */
  nazvyVybranych: Record<string, string>;
  onZmena: (k: string, s: StavHrany, nazov?: string) => void;
  /**
   * Ktorá doska z cenníka je priradená danému dekoru.
   *
   * Nesie aj popis, nie len id: hlavička skupiny ukazovala reťazec
   * z TopSolidu (`8681`), hoci doska už priradená bola. User: *„čo je
   * hore materiál 8681 — aby si to prevzal už z priradeného… malo by
   * tam byť napísané podľa toho, čo vyberieš."*
   */
  doskaPreToken: (token: string) => PriradenaDoska | null;
  /** Čím predvyplniť formulár, keď sa hrana zakladá do cenníka. */
  predvyplneniePre?: (p: MapovaniePolozka) => Predvyplnenie;
}): JSX.Element | null {
  const [editovana, setEditovana] = useState<MapovaniePolozka | null>(null);
  const skupiny = useMemo(() => skupinyHran(hrany), [hrany]);

  if (!skupiny.length) return null;

  return (
    <section className="space-y-3">
      {skupiny.map((g) => (
        <SkupinaHran
          key={g.token}
          skupina={g}
          stavy={stavy}
          nazvyVybranych={nazvyVybranych}
          doska={doskaPreToken(g.token)}
          onKlik={setEditovana}
          onZmena={onZmena}
        />
      ))}

      <VyberPolozkyDialog
        polozka={editovana}
        stav={editovana ? (stavy[kluc(editovana)] ?? PRAZDNY_STAV) : PRAZDNY_STAV}
        // Šírka, nie hrúbka (KOLO 120): appka si sama nahrádza
        // **hrúbku** pásky, keď ju dekor nemá (`auto_nahradena` =
        // `nahradena_hrubka`), takže filter na hrúbku by skryl práve
        // tú hranu, ktorú vybrala. Šírka plynie z hrúbky dielca a je
        // daná. Prepínač sa dá vypnúť.
        filter={{
          druh: "dlzkovy",
          sekcia: "hrany",
          sirkaMm: editovana?.sirka_hrany_mm
            ? String(editovana.sirka_hrany_mm)
            : undefined,
        }}
        predvyplnene={editovana ? predvyplneniePre?.(editovana) : undefined}
        kontext={editovana ? vetaKontextu(editovana) : null}
        autoPopis={
          editovana?.auto_material_id
            ? `Appka vybrala ${editovana.auto_kod || editovana.auto_nazov} podľa dekoru priradenej dosky. Ručná voľba to prebije.`
            : undefined
        }
        onZmena={(s, nazov) => {
          if (editovana) onZmena(kluc(editovana), s, nazov);
        }}
        onClose={() => setEditovana(null)}
      />
    </section>
  );
}

/** Hrúbky dielca jednej položky ako text — „18 a 36 mm", „16, 18 a 36 mm". */
function hrubkyDielcaPopis(p: MapovaniePolozka): string {
  if (!p.hrubky_dielca.length) return "neznáma hrúbka";
  const h = unikatneHrubky(p.hrubky_dielca).map((x) =>
    x === NEZNAMA ? "?" : mm(x),
  );
  if (h.length === 1) return `${h[0]} mm`;
  return `${h.slice(0, -1).join(", ")} a ${h[h.length - 1]} mm`;
}

/** Rozmer pásky z exportu — „42 × 0,5". */
function rozmerHranyPopis(p: MapovaniePolozka): string {
  const s = p.sirka_hrany_mm == null ? "?" : String(p.sirka_hrany_mm);
  const h = p.hrubka_hrany_mm == null ? "?" : mm(p.hrubka_hrany_mm);
  return `${s} × ${h}`;
}

/** Najmenšia známa hrúbka dielca — kľúč na zoradenie riadkov. */
function prvaHrubka(p: MapovaniePolozka): number {
  const cisla = p.hrubky_dielca
    .map((h) => Number(h))
    .filter((n) => Number.isFinite(n));
  return cisla.length ? Math.min(...cisla) : Number.POSITIVE_INFINITY;
}

/**
 * Jeden riadok na hranu z exportu — **nie mriežka** (KOLO 122).
 *
 * User: *„pri tom W960 neviem dať do hrúbky 18 mm hranu 0,5, ukazuje tam
 * '—'. Prečo to tam je? Potom to isté je tam 3×, čo to znamená?"*
 *
 * Do vtedy sa kreslil kartézsky súčin (hrúbky dielcov × hrúbky pások),
 * takže tri hrany z exportu dali tri obsadené bunky a **tri prázdne**.
 * Prázdna pomlčka znamenala „v exporte pre túto kombináciu nič nebolo"
 * a nedala sa kliknúť; červená „— nepriradené" znamenala niečo úplne
 * iné (položka je, chýba jej materiál) a kliknúť sa dala. Dve pomlčky
 * vedľa seba, každá o niečom inom.
 *
 * Zoznam ten problém nemá, lebo neexistuje bunka bez položky. Doplniť
 * hranu, ktorá v exporte nebola, sa tu aj tak nedá zmysluplne: export
 * určuje hrany **per dielec a per strana**, takže by nebolo známe, na
 * čo ju nalepiť. Od toho je slot editor po importe („+ Pridať hrúbku").
 */
/** Rôzne odkazy na vybrané hrany skupiny — jeden na rodinu hrany. */
function odkazySkupiny(
  polozky: MapovaniePolozka[],
): { kod: string; odkaz: string }[] {
  const out = new Map<string, string>();
  for (const p of polozky) {
    if (!p.auto_eshop_odkaz) continue;
    const kod = (p.auto_eshop_kod || "Hranipex").replace(/ lep\.$/, "");
    if (!out.has(kod)) out.set(kod, p.auto_eshop_odkaz);
  }
  return [...out].map(([kod, odkaz]) => ({ kod, odkaz }));
}

function SkupinaHran({
  skupina,
  stavy,
  nazvyVybranych,
  doska,
  onKlik,
  onZmena,
}: {
  skupina: Skupina;
  stavy: Record<string, StavHrany>;
  nazvyVybranych: Record<string, string>;
  doska: PriradenaDoska | null;
  onKlik: (p: MapovaniePolozka) => void;
  onZmena: (k: string, s: StavHrany) => void;
}): JSX.Element {
  const riadky = [...skupina.polozky].sort((a, b) => {
    const rozdiel = prvaHrubka(a) - prvaHrubka(b);
    if (rozdiel !== 0 && Number.isFinite(rozdiel)) return rozdiel;
    return (
      (Number(a.hrubka_hrany_mm) || 0) - (Number(b.hrubka_hrany_mm) || 0)
    );
  });

  return (
    <div className="overflow-hidden rounded-lg border">
      <h4 className="flex flex-wrap items-center gap-2 border-b bg-background-alt px-3 py-1.5 text-sm font-medium">
        {/* Priradená doska z cenníka, kým ju niet tak reťazec z exportu.
            Ten sa nechá ako stlmená stopa aj po priradení — bez neho sa
            nedá dohľadať, ako to volal TopSolid, a to je pri hľadaní
            chyby v modeli prvé, čo človek potrebuje. */}
        <span className="font-mono">{doska?.popis || skupina.nazov}</span>
        <span className="text-xs font-normal text-muted-foreground">
          {doska?.popis ? `z exportu: ${skupina.nazov} · ` : ""}
          {skupina.polozky.length} hrán z exportu
        </span>
        {/* Odkaz na hranu, ktorú appka vybrala, u dodávateľa — „že si to
            viem rovno skontrolovať" (KOLO 135). Jedna rodina (`HD 211381`)
            = jeden odkaz, URL skladá backend zo šablóny v Nastaveniach. */}
        {odkazySkupiny(skupina.polozky).map((o) => (
          <a
            key={o.kod}
            href={o.odkaz}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-xs font-normal text-primary hover:underline"
            title="Otvoriť hranu u dodávateľa"
          >
            {o.kod} ↗
          </a>
        ))}
      </h4>

      <Table>
        <TableHeader>
          <TableRow>
            {/* Kľúčom riadku je **hrana z exportu** (`kluc_hrany` na
                serveri = kód hrany × dekor), nie hrúbka dielca. Kým
                stála hrúbka prvá, čítala sa ako kľúč a opakované
                „18, 36 mm" vyzeralo ako duplicitné riadky — pritom to
                boli tri rôzne pásky (KOLO 124). */}
            <TableHead className="w-[140px]">Hrana z exportu</TableHead>
            <TableHead>Priradené</TableHead>
            <TableHead className="w-[170px]">Použitá na</TableHead>
            {/* Koľko metrov tej hrany je v exporte (KOLO 216) — strany
                × kusy, bez nadmiery pásky, ako súhrn hrán v zákazke */}
            <TableHead className="w-[80px] text-right">Dĺžka</TableHead>
            {/* Ten istý prepínač ako pri materiáloch (KOLO 204) — do
                vtedy sa hrana dala zapamätať len vo výberovom okne */}
            <TableHead className="w-[128px]">Zapamätať</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {riadky.map((p) => (
            <TableRow key={kluc(p)}>
              <TableCell className="whitespace-nowrap px-3 py-1.5 font-mono text-xs">
                {rozmerHranyPopis(p)}
              </TableCell>
              <TableCell className="px-3 py-1.5 text-xs">
                <BunkaHrany
                  polozka={p}
                  stav={stavy[kluc(p)] ?? PRAZDNY_STAV}
                  nazovVybraneho={nazvyVybranych[kluc(p)]}
                  onKlik={() => onKlik(p)}
                />
              </TableCell>
              {/* Kde sa tá páska použila — informácia, nie kľúč, preto
                  stlmená a posledná */}
              <TableCell className="whitespace-nowrap px-3 py-1.5 text-xs text-muted-foreground">
                {hrubkyDielcaPopis(p)}
              </TableCell>
              <TableCell className="whitespace-nowrap px-3 py-1.5 text-right text-xs tabular-nums">
                {metreHrany(p.dlzka_mm)}
              </TableCell>
              <TableCell className="px-3 py-1.5">
                <ZapamatatPrepinac
                  polozka={p}
                  stav={stavy[kluc(p)] ?? PRAZDNY_STAV}
                  onZmena={(s) => onZmena(kluc(p), s)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <DetegovaneHrany doskaId={doska?.id ?? null} />
    </div>
  );
}

/** Jedna bunka — čo sa na tú hranu reálne priradí. */
function BunkaHrany({
  polozka,
  stav,
  nazovVybraneho,
  onKlik,
}: {
  polozka: MapovaniePolozka;
  stav: StavHrany;
  nazovVybraneho: string | undefined;
  onKlik: () => void;
}): JSX.Element {
  const ziadane = `${polozka.sirka_hrany_mm ?? "?"}×${mm(polozka.hrubka_hrany_mm)}`;

  let obsah: JSX.Element;
  if (stav.zmazat) {
    obsah = (
      <>
        <span className="text-muted-foreground">nenahráva sa</span>
        {jeZapamatane(polozka, stav) ? (
          <span className="ml-1.5">
            <ZapamataneStitok />
          </span>
        ) : null}
      </>
    );
  } else if (stav.material_id && jeZapamatane(polozka, stav)) {
    // Hodnota z pamäte priamo v riadku — bez kliknutia (KOLO 204)
    obsah = (
      <>
        <span className="font-mono">{polozka.naucene_kod || polozka.naucene_nazov}</span>
        <span className="ml-1.5">
          <ZapamataneStitok />
        </span>
      </>
    );
  } else if (jeZoZakazky(polozka, stav)) {
    obsah = (
      <>
        <span className="font-mono">{polozka.zakazka_kod || polozka.zakazka_nazov}</span>
        <span className="ml-1.5">
          <ZoZakazkyStitok />
        </span>
      </>
    );
  } else if (stav.material_id) {
    obsah = (
      <>
        <span className="font-mono">{nazovVybraneho ?? "vybraté ručne"}</span>
        <Badge variant="secondary" className="ml-1.5">
          ručne
        </Badge>
      </>
    );
  } else if (polozka.auto_material_id) {
    obsah = (
      <>
        <span className="font-mono">
          {polozka.auto_kod || polozka.auto_nazov}
        </span>
        {polozka.auto_rozmer ? (
          <span className="ml-1 text-muted-foreground">
            ({polozka.auto_rozmer})
          </span>
        ) : null}
        {polozka.auto_nahradena ? (
          <Badge
            variant="outline"
            className="ml-1.5 border-amber-400 text-amber-700"
            title="Dekor túto hrúbku nemá — použila sa najbližšia dostupná. Je to aj v zozname na opravu."
          >
            nahradené
          </Badge>
        ) : null}
      </>
    );
  } else {
    obsah = (
      <span className="italic text-destructive">— nepriradené</span>
    );
  }

  return (
    <button
      type="button"
      onClick={onKlik}
      aria-label={`Hrana ${ziadane} — ${polozka.material_popis}`}
      title={`Export žiada ${ziadane} mm · klik = vybrať inú hranu`}
      className="block max-w-full truncate text-left hover:underline"
    >
      {obsah}
    </button>
  );
}

/**
 * Cena SKU — jedna hodnota, alebo rozpätie, keď sa zlúčené riadky líšia.
 *
 * Líšia sa v ~1,6 % skupín (zmerané na produkčnom cenníku), ale zamlčať
 * to nemožno: účtovala by sa iná cena, než je vidieť.
 */
function formatCenaRozsah(
  min: string | number | null | undefined,
  max: string | number | null | undefined,
): string {
  if (min == null) return "";
  const a = Number(min);
  const b = max == null ? a : Number(max);
  return a === b ? formatEur(a) : `${formatEur(a)} – ${formatEur(b)}`;
}

/**
 * Čo appka pre dekor v cenníku našla — zbalené, ako v slot dialógu.
 *
 * Je to **prehľad, nie otázka**: priraďuje sa klikom do tabuľky vyššie.
 */
function DetegovaneHrany({ doskaId }: { doskaId: string | null }): JSX.Element | null {
  const query = useEdgeCandidates(doskaId ?? undefined);
  const strukturaLabels = useStrukturaLabels();
  const kandidati = query.data?.data?.candidates ?? [];

  if (!doskaId) {
    return (
      <p className="border-t px-3 py-1.5 text-[11px] text-muted-foreground">
        Detegované hrany sa ukážu, keď bude priradená doska tohto dekoru.
      </p>
    );
  }
  if (!kandidati.length) return null;

  return (
    <details className="border-t">
      <summary className="cursor-pointer select-none px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground">
        Detegované hrany ({kandidati.length})
      </summary>
      <ul className="divide-y border-t text-xs">
        {kandidati.map((k) => {
          // Riadok je **jedno SKU**; zlúčené laminodosky sa líšia len
          // štruktúrou, tak sú tu všetky (KOLO 123)
          const zoznam = k.struktury?.length
            ? k.struktury
            : [k.edge.struktura_povrchu];
          const struktury = zoznam
            .map((x) => formatStruktura(x ?? null, strukturaLabels))
            .filter((x): x is string => Boolean(x));
          return (
            <li key={k.edge.id} className="flex items-center gap-2 px-3 py-1">
              <span className="font-mono">
                {rozmerHrany(k.edge.sirka_mm, k.edge.hrubka_mm)}
              </span>
              <span className="font-mono text-muted-foreground">
                {materialPrimaryId(k.edge) ?? ""}
              </span>
              <span className="truncate">{k.edge.nazov}</span>
              <span className="ml-auto whitespace-nowrap font-mono text-[10px] text-muted-foreground">
                {formatCenaRozsah(k.cena_min, k.cena_max)}
              </span>
              {struktury.map((st) => (
                <span
                  key={st}
                  className="whitespace-nowrap rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground"
                >
                  {st}
                </span>
              ))}
            </li>
          );
        })}
      </ul>
    </details>
  );
}
